/*
 * Centralized carousel behavior — the single `<theme-carousel>` custom
 * element every horizontally-scrolling carousel in this theme (featured
 * collection, why us, and any future one) wires itself up from, instead of
 * each section re-implementing its own copy. See AGENTS.md § Carousel
 * (`<theme-carousel>`) for the full write-up.
 *
 * The scrolling itself is always real native browser `overflow-x` — this
 * script never drives the track via `transform`/an animation library, only
 * `viewport.scrollLeft`/`scrollTo()`. That's deliberate and load-bearing:
 * see AGENTS.md § Featured collection for the Slick/Embla history this
 * replaced and why. Touch and trackpad scrolling need NONE of this script;
 * they already work from a plain page load via CSS `overflow-x: auto`
 * alone. This element's job is only: prev/next buttons, a progress-bar
 * indicator synced to real `scrollLeft`, and click-and-drag-to-scroll for
 * mouse input (the one thing native `overflow-x` doesn't give you for
 * free).
 *
 * Loaded as a plain classic script (NOT a module, NOT inside a
 * `{% javascript %}` tag) via layout/theme.liquid, same pattern as
 * assets/cart.js — see that file's own doc comment for why (custom
 * elements upgrade whatever markup shows up later regardless of load
 * order, so this doesn't need the same "before content_for_header"
 * guarantee cart.js needs, but staying consistent with the theme's one
 * other centralized-script precedent is simpler than inventing a second
 * loading convention).
 *
 * Markup contract (see sections/featured-collection.liquid and
 * sections/why-us.liquid for real examples):
 *
 *   <theme-carousel
 *     data-carousel-desktop="true|false"
 *     data-carousel-mobile="true|false"
 *     data-carousel-id="optional-unique-id"
 *   >
 *     <div data-carousel-viewport>
 *       <div data-carousel-track>...slides as direct children...</div>
 *     </div>
 *     <div data-carousel-progress-fill></div>   (optional)
 *     <button data-carousel-prev>...</button>   (optional, can repeat)
 *     <button data-carousel-next>...</button>   (optional, can repeat)
 *     <button data-carousel-dot>...</button>    (optional, one per slide, in slide order)
 *   </theme-carousel>
 *
 * **Dots (`[data-carousel-dot]`) are a direct index-jump pager — sections/
 * marquee.liquid's mobile nav is the first user of these** (a dot per
 * slide, not prev/next). Render exactly one dot per slide, in the same
 * order as the slides — clicking dot N calls `scrollToIndex(N)` the same
 * way a prev/next click does (absolute target, optimistic nav-state
 * update, see below). `.is-active` (and `aria-current="true"`) is kept on
 * whichever dot is nearest the current real `scrollLeft` — every `scroll`
 * event, and optimistically the moment a dot/prev/next click fires, same
 * "don't wait on the scroll event alone" reasoning as the buttons' own
 * disabled-state. `carousel.js` only ever toggles that class/attribute; a
 * dot's actual appearance (size, color, the "growing pill" active state)
 * is entirely the section's own CSS, same division of ownership the
 * progress-bar fill already has.
 *
 * - `data-carousel-desktop`/`data-carousel-mobile` gate whether the
 *   button/progress-bar JS is active at that breakpoint — a plain `grid`
 *   layout (featured-collection's grid mode) passes "false" for whichever
 *   breakpoint isn't a carousel there; a section with no desktop carousel
 *   at all (why us) just always passes "false" for desktop. The scrolling
 *   itself is native regardless — this only gates the JS enhancement.
 * - Prev/next buttons can live OUTSIDE the element (featured-collection
 *   renders a second nav row in its header, sibling to
 *   `<theme-carousel>`, not a descendant of it — see that section's top
 *   comment for why). Give the element a `data-carousel-id` and the
 *   external button `data-carousel-target="<same id>"` to pair them; no id
 *   needed when every button is a normal descendant.
 * - Everything is measured live off the real DOM on every call (slide
 *   count, card pitch, scroll position) — nothing is cached from
 *   `connectedCallback` — so this naturally stays correct across a layout
 *   change, not just a fresh page load.
 *
 * **Deliberately no CSS scroll-snap anywhere in this theme's carousels —
 * a developer request.** A drag/swipe/trackpad-scroll release stops
 * wherever its momentum leaves it; the prev/next buttons are the one thing
 * that still land exactly on a card, via `scrollToIndex()` below (an
 * ABSOLUTE target — a slide's position in the viewport's own scroll
 * coordinate space, see `slideLeft()` — not a relative
 * `scrollBy(cardPitch)`), so a click always ends precisely aligned on a
 * card regardless of whatever un-aligned position a manual drag left the
 * viewport at.
 *
 * **Button disabled-state/progress-bar updates optimistically on click,
 * not only from the `scroll` event — this fixed a real bug.** The
 * original per-section implementation this replaced updated `disabled`
 * purely from a `scroll` listener; in practice that left the PREV button
 * stuck `disabled` (and therefore inert — `pointer-events: none` on
 * `:disabled` swallows the click before any handler runs) after using
 * NEXT from the very start, on at least some runs — the `scroll` event
 * following a programmatic `scrollTo()` isn't a guaranteed synchronous
 * signal to depend on alone. `scrollToIndex()` now calls `setNavState()`
 * immediately with the PREDICTED resulting `scrollLeft` (`target` clamped
 * to the real scroll bounds, since `scrollTo()` clamps out-of-range
 * targets on its own) rather than waiting for the `scroll` event alone —
 * the `scroll` listener still resyncs continuously afterward (for
 * drag-driven movement, and to correct the optimistic guess once the
 * animation actually settles), it's just no longer the ONLY path to an
 * enabled prev button.
 *
 * **Both the progress-bar fill AND button disabled-state are driven by
 * real `scrollLeft` against the real scroll bounds — NOT by a rounded
 * nearest-card index.** An earlier pass computed `atStart`/`atEnd` from
 * `Math.round(scrollLeft / cardPitch)`, which was a real, confirmed bug
 * with two symptoms: next never disabled at the true end (whenever more
 * than one card is visible at once — any desktop carousel — the browser
 * caps real `scrollLeft` at `maxScrollLeft()` before that rounded index
 * ever reaches `slideCount - 1`), and prev disabled too early / "didn't
 * work sometimes" (any `scrollLeft` within half a `cardPitch()` of 0
 * rounded down to index 0 and disabled the button — swallowing the click
 * — even with real room left to scroll). See `setNavState()`'s and
 * `updateProgress()`'s own comments below for the fix.
 *
 * **Re-evaluates on every breakpoint crossing, not just once at
 * `connectedCallback`** — this is what makes resizing across the 749px
 * breakpoint switch cleanly between carousel/non-carousel (or
 * mobile-enhanced/desktop-enhanced) live, instead of requiring a page
 * reload. `_setup()` is fully re-runnable: it tears down every listener
 * it previously added (via one shared `AbortController` per setup pass,
 * aborted at the top of the next pass) before wiring fresh ones, so a
 * window resized back and forth across the breakpoint several times
 * doesn't pile up duplicate listeners.
 */
(function () {
  'use strict';

  if (customElements.get('theme-carousel')) return;

  var MOBILE_QUERY = '(max-width: 749px)';

  function slidesOf(track) {
    return Array.prototype.slice.call(track.children);
  }

  class TheCarousel extends HTMLElement {
    connectedCallback() {
      this._mq = window.matchMedia(MOBILE_QUERY);
      this._onMqChange = this._setup.bind(this);
      // 'change' fires on every crossing of the breakpoint in EITHER
      // direction — this is the whole fix for "resizing doesn't
      // retroactively wire/unwire the carousel."
      if (this._mq.addEventListener) {
        this._mq.addEventListener('change', this._onMqChange);
      } else if (this._mq.addListener) {
        // Safari <14 fallback — matchMedia predates addEventListener support.
        this._mq.addListener(this._onMqChange);
      }
      this._setup();
    }

    disconnectedCallback() {
      if (this._mq) {
        if (this._mq.removeEventListener) {
          this._mq.removeEventListener('change', this._onMqChange);
        } else if (this._mq.removeListener) {
          this._mq.removeListener(this._onMqChange);
        }
      }
      if (this._controller) this._controller.abort();
    }

    _resolveButtons(attr) {
      var internal = Array.prototype.slice.call(this.querySelectorAll('[' + attr + ']'));
      var id = this.dataset.carouselId;
      if (!id) return internal;
      var external = Array.prototype.slice.call(
        document.querySelectorAll('[' + attr + '][data-carousel-target="' + id + '"]'),
      );
      return internal.concat(external);
    }

    _setup() {
      // Fully re-runnable — see the file's top comment. Abort whatever the
      // previous pass wired before wiring the new one.
      if (this._controller) this._controller.abort();
      var controller = new AbortController();
      this._controller = controller;
      var signal = controller.signal;

      var viewport = this.querySelector('[data-carousel-viewport]');
      var track = this.querySelector('[data-carousel-track]');
      if (!viewport || !track) return;

      var isMobile = this._mq.matches;
      var desktopActive = this.dataset.carouselDesktop === 'true';
      var mobileActive = this.dataset.carouselMobile === 'true';
      var shouldEnhance = isMobile ? mobileActive : desktopActive;

      var progressFill = this.querySelector('[data-carousel-progress-fill]');
      var prevButtons = this._resolveButtons('data-carousel-prev');
      var nextButtons = this._resolveButtons('data-carousel-next');
      var dotButtons = this._resolveButtons('data-carousel-dot');

      // 1px slack for comparing a slide's position against real
      // `scrollLeft` — subpixel/fractional layout values (confirmed live)
      // mean an exact `<`/`>` comparison can misfire by a hair even when
      // the viewport is genuinely flush with a card's edge.
      var EDGE_EPSILON = 1;

      // A slide's left edge, in the VIEWPORT's own scroll coordinate
      // space — i.e. the `scrollLeft` that puts this slide flush against
      // the viewport's left edge. **Deliberately NOT `slide.offsetLeft` —
      // a real, confirmed bug.** `offsetLeft` is relative to the slide's
      // nearest POSITIONED ancestor, which on the real page is an element
      // OUTSIDE the scrolling viewport (neither the viewport nor the
      // track establishes its own positioning context here) — confirmed
      // live: at `scrollLeft: 0`, with the first card sitting flush
      // against the viewport's own left edge, its `offsetLeft` still read
      // `36` (the page container's side padding), not `0`. Every slide
      // carried that same +36 baseline, so `scrollToIndex(0)` — "go to
      // the very first card" — was scrolling 36px PAST true 0 instead of
      // landing on it. That's the reported bug: clicking next from a
      // fresh page load moved "a tiny bit" (that stray 36px) rather than
      // a full card, and the newly-scrolled-to card's left edge ended up
      // exactly 36px short of flush — cut off. `getBoundingClientRect()`
      // is relative to the VIEWPORT (the browser's visual viewport, not
      // this component's), which shifts by exactly `scrollLeft` as the
      // user scrolls — subtracting the two rects' `left` cancels that
      // shift out, and adding the CURRENT `scrollLeft` back converts the
      // result into "the scrollLeft that would put this slide at that
      // same on-screen position," independent of `offsetParent` entirely.
      function slideLeft(slide) {
        return slide.getBoundingClientRect().left - viewport.getBoundingClientRect().left + viewport.scrollLeft;
      }

      // Last slide whose start is (with slack) STRICTLY BEFORE the current
      // scroll position — i.e. "the previous card," for the prev button.
      //
      // **Deliberately NOT `Math.round(scrollLeft / cardPitch) - 1` — a
      // real, confirmed bug, and the reason the previous card used to get
      // cut off from the left after pressing prev from the final card.**
      // With no CSS scroll-snap (see the file's top comment), a "next"
      // click can clamp `scrollLeft` at `maxScrollLeft()` well short of
      // `(slideCount - 1) * cardPitch` — whenever more than one card is
      // visible at once (any desktop carousel), the last card doesn't
      // need a full pitch of scroll to become fully visible, so the real
      // end position doesn't land on any card's exact grid boundary.
      // Rounding that off-grid `scrollLeft` to a "nearest" index, then
      // subtracting 1, could skip over the card that was ALREADY only
      // partially visible at the true end — the actual "previous" card
      // the user needed revealed — landing one card further back instead,
      // with a DIFFERENT card now cut off on the other side. Reading each
      // slide's real viewport-relative position via `slideLeft()` above
      // (rather than a uniform-pitch assumption) always targets the
      // specific card whose start hasn't been reached yet, snapping it
      // fully into view with nothing cut off, regardless of whether the
      // current position sits on the card grid or not.
      function slideIndexBefore(scrollLeft) {
        var slides = slidesOf(track);
        var index = 0;
        for (var i = 0; i < slides.length; i++) {
          if (slideLeft(slides[i]) < scrollLeft - EDGE_EPSILON) {
            index = i;
          } else {
            break;
          }
        }
        return index;
      }

      // First slide whose start is (with slack) STRICTLY AFTER the current
      // scroll position — i.e. "the next card," for the next button. Same
      // real-position approach as `slideIndexBefore()` above, for the same
      // reason — see that function's comment.
      function slideIndexAfter(scrollLeft) {
        var slides = slidesOf(track);
        for (var i = 0; i < slides.length; i++) {
          if (slideLeft(slides[i]) > scrollLeft + EDGE_EPSILON) {
            return i;
          }
        }
        return slides.length - 1;
      }

      // Progress-bar fill position — driven by the viewport's REAL
      // `scrollLeft`, continuously, not by the nearest-card `index` passed
      // to `setNavState()` below. An index-based fill only moves in
      // discrete per-card jumps (it can't show any position until a drag
      // has crossed at least one full `cardPitch()`), which reads as the
      // indicator "not respecting exactly where the user is" during a
      // drag — confirmed live: scrolling to 50% of the true max scroll
      // distance left the fill sitting at 0% because that 50% hadn't yet
      // reached the first card boundary. This is called on every `scroll`
      // event (see below) so it stays continuous with the actual drag, not
      // just on release.
      function updateProgress() {
        if (!progressFill) return;
        var slideCount = slidesOf(track).length;
        if (slideCount <= 1) {
          progressFill.style.width = '100%';
          progressFill.style.left = '0%';
          return;
        }
        var fillWidthPct = 100 / slideCount;
        var maxScroll = viewport.scrollWidth - viewport.clientWidth;
        var progress = maxScroll > 0 ? viewport.scrollLeft / maxScroll : 0;
        progress = Math.max(0, Math.min(1, progress));
        progressFill.style.width = fillWidthPct + '%';
        progressFill.style.left = progress * (100 - fillWidthPct) + '%';
      }

      // Slide nearest the given `scrollLeft` — used only for dot-pagination
      // active state, so "nearest" (not "before"/"after" like the prev/next
      // helpers above) is the right question here: a dot represents "you
      // are currently on slide N," not a scroll direction.
      function nearestSlideIndex(scrollLeft) {
        var slides = slidesOf(track);
        var nearest = 0;
        var nearestDistance = Infinity;
        for (var i = 0; i < slides.length; i++) {
          var distance = Math.abs(slideLeft(slides[i]) - scrollLeft);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = i;
          }
        }
        return nearest;
      }

      // Dot active-state — mirrors `updateProgress()`'s "driven by real
      // scrollLeft, called on every scroll event and optimistically on
      // click" approach, just for a discrete per-dot class/attribute
      // instead of a continuous fill width. A no-op when the section
      // renders no dots (`dotButtons.length === 0`), same harmless-no-op
      // shape as an empty prev/next button list.
      function updateDots(scrollLeft) {
        if (!dotButtons.length) return;
        var active = nearestSlideIndex(scrollLeft);
        dotButtons.forEach(function (btn, i) {
          if (i === active) {
            btn.classList.add('is-active');
            btn.setAttribute('aria-current', 'true');
          } else {
            btn.classList.remove('is-active');
            btn.removeAttribute('aria-current');
          }
        });
      }

      // Real max scroll distance — `EDGE_EPSILON` (declared above,
      // alongside `slideIndexBefore`/`slideIndexAfter`) covers the same
      // fractional/subpixel `scrollLeft`/`scrollWidth`/`clientWidth`
      // slack here too, so an exact `=== 0` / `=== maxScroll` comparison
      // doesn't stay false forever even once the user has genuinely
      // reached either end.
      function maxScrollLeft() {
        return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      }

      // Whether there's genuinely more content than fits — i.e. whether a
      // scroll input (drag, trackpad, wheel) can move the viewport at all
      // right now. Toggles `.is-scrollable` on the viewport so a section's
      // own CSS can show a grab cursor ONLY when that's actually true (see
      // sections/why-us.liquid's own use of this — a plain cursor
      // otherwise, since a grab affordance over a row that can't move is
      // misleading). Deliberately computed and kept live even when
      // `shouldEnhance` is false below (why-us's own desktop breakpoint,
      // `data-carousel-desktop="false"`) — the cursor should still reflect
      // real scrollability there, even though the button/progress-bar/
      // drag-to-scroll JS enhancement itself is intentionally off.
      function updateScrollable() {
        viewport.classList.toggle('is-scrollable', maxScrollLeft() > EDGE_EPSILON);
      }
      updateScrollable();

      // Re-checks on resize — a card row can cross from "overflowing" to
      // "everything fits" (or back) purely from the viewport getting wider
      // or narrower, with no breakpoint crossing and so no `_setup()`
      // re-run (that only happens on the mobile/desktop `matchMedia`
      // change — see `connectedCallback` above). rAF-throttled the same
      // way the `scroll` listener further below is, so a drag-resize
      // doesn't thrash this on every intermediate pixel.
      var scrollableResizeTicking = false;
      window.addEventListener(
        'resize',
        function () {
          if (scrollableResizeTicking) return;
          scrollableResizeTicking = true;
          requestAnimationFrame(function () {
            updateScrollable();
            scrollableResizeTicking = false;
          });
        },
        { signal: signal },
      );

      // Button disabled-state — driven by real `scrollLeft` against the
      // real scroll bounds, NOT the nearest-card index `updateProgress()`
      // avoids for the same reason (see its comment above). Using a
      // rounded `Math.round(scrollLeft / cardPitch)` index here (a
      // `cardPitch()`/`currentIndex()` pair that no longer exists in this
      // file — see `slideIndexBefore()`/`slideIndexAfter()` above for
      // their real-offset replacement) was a real, confirmed bug with two
      // symptoms:
      //
      // 1. **Next never disabled at the true end.** Whenever more than
      //    one card is visible at once (any desktop carousel), the
      //    browser caps real `scrollLeft` at `maxScrollLeft()` — reachable
      //    well BEFORE `scrollLeft` would hit `(slideCount - 1) *
      //    cardPitch()`, since the last card doesn't need a full pitch of
      //    scroll to become fully visible once several cards already fit
      //    in the viewport. `currentIndex()`'s rounded value therefore
      //    never reached `slideCount - 1` at the real end, so `atEnd` was
      //    permanently false and the next button stayed clickable
      //    (visibly doing nothing) past the last card.
      // 2. **Prev disabled too early, and "doesn't work sometimes."** The
      //    same rounding works the other way at the start: any
      //    `scrollLeft` within half a `cardPitch()` of 0 — e.g. the tail
      //    end of a partial drag — rounds `currentIndex()` down to 0,
      //    setting `atStart` (and therefore `disabled`, which also sets
      //    `pointer-events: none`) even though real `scrollLeft` was still
      //    well above 0 and there was more room to scroll left. That's
      //    exactly the "prev button doesn't work sometimes" report: the
      //    click wasn't silently failing, the button was already
      //    (incorrectly) disabled and swallowing the click before any
      //    handler ran.
      //
      // Real `scrollLeft` against real bounds has neither problem: it only
      // reports atStart/atEnd exactly at 0%/100% of the actual scrollable
      // range, matching the progress bar's own thumb position 1:1.
      function setNavState(scrollLeft) {
        var maxScroll = maxScrollLeft();
        var atStart = scrollLeft <= EDGE_EPSILON;
        var atEnd = maxScroll <= EDGE_EPSILON || scrollLeft >= maxScroll - EDGE_EPSILON;
        prevButtons.forEach(function (btn) { btn.disabled = atStart; });
        nextButtons.forEach(function (btn) { btn.disabled = atEnd; });
      }

      // ABSOLUTE target (`slideLeft()`, a slide's position in the
      // viewport's own scroll coordinate space — see that function's
      // comment for why this isn't plain `offsetLeft`), not a relative
      // `scrollBy(cardPitch)` — see the file's top comment for why. Also
      // updates nav state immediately rather than waiting for the `scroll`
      // event alone — see the top comment's note on the stuck-disabled-
      // prev-button bug this fixes. The optimistic value passed to
      // `setNavState()` is the PREDICTED real `scrollLeft` once the
      // browser clamps `target` to `maxScrollLeft()` (`scrollTo` clamps
      // out-of-range targets on its own) — not the raw target — so a next
      // click that lands on the last slide is correctly predicted as
      // "reached the true end" immediately, without waiting on a `scroll`
      // event that may fire after the animation and after this function's
      // caller has already returned.
      function scrollToIndex(index) {
        var slides = slidesOf(track);
        var clamped = Math.max(0, Math.min(index, slides.length - 1));
        var target = slides[clamped] ? slideLeft(slides[clamped]) : 0;
        viewport.scrollTo({ left: target, behavior: 'smooth' });
        var predictedScrollLeft = Math.max(0, Math.min(target, maxScrollLeft()));
        setNavState(predictedScrollLeft);
        // Optimistic only — the `scroll` listener below re-syncs
        // `updateProgress()`/`updateDots()` continuously once the
        // smooth-scroll animation actually starts moving `scrollLeft`,
        // same "don't wait on the scroll event alone" reasoning as
        // `setNavState()` above.
        updateProgress();
        updateDots(predictedScrollLeft);
      }

      if (!shouldEnhance) return;

      var ticking = false;
      viewport.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            setNavState(viewport.scrollLeft);
            updateProgress();
            updateDots(viewport.scrollLeft);
            ticking = false;
          });
        },
        { passive: true, signal: signal },
      );

      prevButtons.forEach(function (btn) {
        btn.addEventListener('click', function () { scrollToIndex(slideIndexBefore(viewport.scrollLeft)); }, { signal: signal });
      });
      nextButtons.forEach(function (btn) {
        btn.addEventListener('click', function () { scrollToIndex(slideIndexAfter(viewport.scrollLeft)); }, { signal: signal });
      });
      // Each dot jumps straight to its own slide index — a direct-index
      // pager, unlike prev/next's relative-to-current-position lookup.
      dotButtons.forEach(function (btn, i) {
        btn.addEventListener('click', function () { scrollToIndex(i); }, { signal: signal });
      });

      // Click-and-drag-to-scroll for MOUSE input — native `overflow-x`
      // gives touch/trackpad scrolling for free, but a mouse press-and-
      // drag does NOT scroll a native overflow container on its own. The
      // scrolling itself is still 100% native (`viewport.scrollLeft`
      // written directly, no transform, no animation library) — this just
      // feeds mouse movement into it the way a trackpad/touch gesture
      // already would.
      var dragging = false;
      var dragMoved = false;
      var dragStartX = 0;
      var dragStartScrollLeft = 0;

      // Images/links are natively draggable in every browser — without
      // this, starting a drag ON a card (its image or its own <a>) kicks
      // off the browser's own "drag this out" gesture instead of the
      // scroll-drag below.
      viewport.addEventListener('dragstart', function (event) { event.preventDefault(); }, { signal: signal });

      viewport.addEventListener(
        'mousedown',
        function (event) {
          dragging = true;
          dragMoved = false;
          dragStartX = event.clientX;
          dragStartScrollLeft = viewport.scrollLeft;
          viewport.classList.add('is-dragging');
        },
        { signal: signal },
      );

      window.addEventListener(
        'mousemove',
        function (event) {
          if (!dragging) return;
          var delta = event.clientX - dragStartX;
          // A few px of jitter on what was meant as a click shouldn't
          // count as a drag — only flip to "this was a drag" past a small
          // threshold, so a plain click on a card still navigates
          // normally (see the capturing `click` listener below).
          if (Math.abs(delta) > 5) dragMoved = true;
          viewport.scrollLeft = dragStartScrollLeft - delta;
        },
        { signal: signal },
      );

      window.addEventListener(
        'mouseup',
        function () {
          if (!dragging) return;
          dragging = false;
          viewport.classList.remove('is-dragging');
          // Explicit release-snap for the one section that opts into CSS
          // scroll-snap (see the file's top comment — testimonials only,
          // via `scroll-snap-type` on its own viewport, nothing else in
          // this theme sets it). Feature-detected off the real computed
          // style rather than a new data attribute, so this stays a no-op
          // everywhere else. **Needed because CSS scroll-snap does NOT
          // re-correct a mouse drag on its own — a real, confirmed bug.**
          // Native scroll-snap only re-evaluates in response to an actual
          // browser scroll gesture (touch/trackpad/wheel); this drag isn't
          // one — it's a plain script writing `viewport.scrollLeft` on
          // every `mousemove` above, which the browser doesn't treat as a
          // "scroll operation" for snapping purposes. Confirmed live:
          // releasing a mouse drag mid-slide left `scrollLeft` sitting
          // exactly where the cursor let go, forever — never correcting
          // onto a card the way a touch swipe on the same viewport already
          // does. `scrollToIndex()` below reuses the same absolute-target/
          // smooth-scroll path the prev/next buttons already use, so the
          // release animates onto the nearest card exactly like a snap.
          if (getComputedStyle(viewport).scrollSnapType !== 'none') {
            scrollToIndex(nearestSlideIndex(viewport.scrollLeft));
          }
        },
        { signal: signal },
      );

      // Swallow the click that follows a real drag (capturing phase, so it
      // beats a card's own link click handling) — without this, releasing
      // a drag over a card would also navigate to it.
      viewport.addEventListener(
        'click',
        function (event) {
          if (dragMoved) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        { capture: true, signal: signal },
      );

      setNavState(viewport.scrollLeft);
      updateProgress();
      updateDots(viewport.scrollLeft);
    }
  }

  customElements.define('theme-carousel', TheCarousel);
})();
